import { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useProjectStore } from '../../store/useProjectStore';
import * as api from '../../api/endpoints';
import { CropModal } from './CropModal';

// ── Types ───────────────────────────────────────────────────

interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
  images?: { data: string; label: string }[];
}

interface ChatAttachment {
  filename: string;
  pages: number[];
  cropImage?: string; // base64 PNG
  cropLabel?: string;
}

// ── Component ───────────────────────────────────────────────

export function ProjectChat() {
  const project = useProjectStore((s) => s.project);
  const files = project?.files || [];

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  const [showFilePicker, setShowFilePicker] = useState(false);
  const [cropTarget, setCropTarget] = useState<{ filename: string; page: number } | null>(null);

  const messagesRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages.length, loading]);

  const addAttachment = useCallback((filename: string, pages: number[]) => {
    setAttachments((prev) => {
      // Merge with existing attachment for same file
      const existing = prev.find((a) => a.filename === filename && !a.cropImage);
      if (existing) {
        const merged = new Set([...existing.pages, ...pages]);
        return prev.map((a) =>
          a === existing ? { ...a, pages: [...merged].sort((a, b) => a - b) } : a,
        );
      }
      return [...prev, { filename, pages }];
    });
    setShowFilePicker(false);
  }, []);

  const removeAttachment = useCallback((idx: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleCropCapture = useCallback((base64: string) => {
    if (!cropTarget) return;
    setAttachments((prev) => [
      ...prev,
      {
        filename: cropTarget.filename,
        pages: [cropTarget.page],
        cropImage: base64,
        cropLabel: `${cropTarget.filename} p.${cropTarget.page} (crop)`,
      },
    ]);
    setCropTarget(null);
  }, [cropTarget]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    // Collect images (crops) and attached pages
    const images: { data: string; mime_type: string; label: string }[] = [];
    const attachedPages: { filename: string; pages: number[] }[] = [];

    for (const att of attachments) {
      if (att.cropImage) {
        images.push({
          data: att.cropImage,
          mime_type: 'image/png',
          label: att.cropLabel || `${att.filename} crop`,
        });
      } else {
        attachedPages.push({ filename: att.filename, pages: att.pages });
      }
    }

    const userMsg: ChatMessage = {
      role: 'user',
      text,
      images: images.length > 0 ? images.map((i) => ({ data: i.data, label: i.label })) : undefined,
    };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setAttachments([]);
    setLoading(true);

    try {
      const result = await api.chat(
        text,
        messages.map((m) => ({ role: m.role, text: m.text })),
        images.length > 0 ? images : undefined,
        attachedPages.length > 0 ? attachedPages : undefined,
      );
      if (result?.response) {
        setMessages([...newMessages, { role: 'assistant', text: result.response }]);
      }
    } catch (e) {
      setMessages([...newMessages, { role: 'assistant', text: `Error: ${e}` }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [input, messages, loading, attachments]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  return (
    <>
      <div className={`project-chat ${expanded ? 'expanded' : 'collapsed'}`}>
        <div className="chat-header" onClick={() => setExpanded(!expanded)}>
          <span className="chat-header-title">Project Chat</span>
          {messages.length > 0 && !expanded && (
            <span className="chat-badge">{messages.length}</span>
          )}
          <span className="chat-toggle">{expanded ? '\u25BC' : '\u25B2'}</span>
        </div>
        {expanded && (
          <>
            <div className="chat-messages" ref={messagesRef}>
              {messages.length === 0 && (
                <div className="chat-placeholder">
                  Ask questions about the project. You can attach specific pages or crop regions from plans.
                </div>
              )}
              {messages.map((msg, i) => (
                <div key={i} className={`chat-bubble chat-bubble-${msg.role}`}>
                  {msg.images && msg.images.length > 0 && (
                    <div className="chat-attached-images">
                      {msg.images.map((img, j) => (
                        <img
                          key={j}
                          src={`data:image/png;base64,${img.data}`}
                          alt={img.label}
                          title={img.label}
                          className="chat-attached-thumb"
                        />
                      ))}
                    </div>
                  )}
                  {msg.role === 'assistant' ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                  ) : (
                    <span>{msg.text}</span>
                  )}
                </div>
              ))}
              {loading && (
                <div className="chat-bubble chat-bubble-assistant">
                  <span className="chat-typing">Thinking...</span>
                </div>
              )}
            </div>

            {/* Attachment bar */}
            {attachments.length > 0 && (
              <div className="chat-attachments">
                {attachments.map((att, i) => (
                  <div key={i} className="chat-attachment-chip">
                    {att.cropImage ? (
                      <img
                        src={`data:image/png;base64,${att.cropImage}`}
                        alt="crop"
                        className="chip-thumb"
                      />
                    ) : null}
                    <span className="chip-label">
                      {att.cropLabel || `${att.filename} p.${att.pages.join(',')}`}
                    </span>
                    <button className="chip-remove" onClick={() => removeAttachment(i)}>
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Input row */}
            <div className="chat-input-row">
              <div className="chat-toolbar">
                <button
                  className="chat-tool-btn"
                  title="Attach pages"
                  onClick={() => setShowFilePicker(!showFilePicker)}
                >
                  &#128206;
                </button>
                <button
                  className="chat-tool-btn"
                  title="Crop from page"
                  onClick={() => {
                    // Open crop with first attached file, or first project file
                    const defaultFile = attachments[0]?.filename || files[0]?.filename;
                    if (defaultFile) {
                      const pg = attachments[0]?.pages[0] || 1;
                      setCropTarget({ filename: defaultFile, page: pg });
                    }
                  }}
                >
                  &#9986;
                </button>
              </div>
              <textarea
                ref={inputRef}
                className="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about the project..."
                rows={1}
                disabled={loading}
              />
              <button
                className="chat-send-btn"
                onClick={handleSend}
                disabled={!input.trim() || loading}
              >
                Send
              </button>
            </div>

            {/* File picker dropdown */}
            {showFilePicker && (
              <FilePickerDropdown
                files={files}
                onSelect={addAttachment}
                onCrop={(filename, page) => {
                  setCropTarget({ filename, page });
                  setShowFilePicker(false);
                }}
                onClose={() => setShowFilePicker(false)}
              />
            )}
          </>
        )}
      </div>

      {/* Crop modal */}
      {cropTarget && (
        <CropModal
          filename={cropTarget.filename}
          page={cropTarget.page}
          onCapture={handleCropCapture}
          onClose={() => setCropTarget(null)}
          onPageChange={(page) => setCropTarget((prev) => prev ? { ...prev, page } : null)}
        />
      )}
    </>
  );
}

// ── File Picker ─────────────────────────────────────────────

function FilePickerDropdown({
  files,
  onSelect,
  onCrop,
  onClose,
}: {
  files: { filename: string; page_count: number }[];
  onSelect: (filename: string, pages: number[]) => void;
  onCrop: (filename: string, page: number) => void;
  onClose: () => void;
}) {
  const [expandedFile, setExpandedFile] = useState<string | null>(null);
  const [pageInput, setPageInput] = useState('');

  const parsePages = (input: string, maxPage: number): number[] => {
    const pages: Set<number> = new Set();
    for (const part of input.split(',')) {
      const trimmed = part.trim();
      const range = trimmed.match(/^(\d+)\s*-\s*(\d+)$/);
      if (range) {
        const start = Math.max(1, parseInt(range[1]));
        const end = Math.min(maxPage, parseInt(range[2]));
        for (let i = start; i <= end; i++) pages.add(i);
      } else {
        const n = parseInt(trimmed);
        if (n >= 1 && n <= maxPage) pages.add(n);
      }
    }
    return [...pages].sort((a, b) => a - b);
  };

  return (
    <div className="file-picker-backdrop" onClick={onClose}>
      <div className="file-picker" onClick={(e) => e.stopPropagation()}>
        <div className="file-picker-header">
          <span>Attach project files</span>
          <button className="file-picker-close" onClick={onClose}>&times;</button>
        </div>
        <div className="file-picker-list">
          {files.map((f) => (
            <div key={f.filename} className="file-picker-item">
              <div
                className="file-picker-name"
                onClick={() => setExpandedFile(expandedFile === f.filename ? null : f.filename)}
              >
                <span>{f.filename}</span>
                <span className="file-picker-pages">{f.page_count} pages</span>
              </div>
              {expandedFile === f.filename && (
                <div className="file-picker-page-select">
                  <input
                    type="text"
                    className="file-picker-page-input"
                    placeholder={`Pages (e.g. 1,3,5-8) — max ${f.page_count}`}
                    value={pageInput}
                    onChange={(e) => setPageInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const pages = parsePages(pageInput, f.page_count);
                        if (pages.length > 0) {
                          onSelect(f.filename, pages);
                          setPageInput('');
                        }
                      }
                    }}
                    autoFocus
                  />
                  <div className="file-picker-actions">
                    <button
                      className="fp-btn"
                      onClick={() => {
                        const pages = pageInput.trim()
                          ? parsePages(pageInput, f.page_count)
                          : Array.from({ length: f.page_count }, (_, i) => i + 1);
                        if (pages.length > 0) {
                          onSelect(f.filename, pages);
                          setPageInput('');
                        }
                      }}
                    >
                      {pageInput.trim() ? 'Attach pages' : 'Attach all'}
                    </button>
                    <button
                      className="fp-btn fp-btn-crop"
                      onClick={() => {
                        const pg = pageInput.trim() ? parseInt(pageInput) || 1 : 1;
                        onCrop(f.filename, pg);
                      }}
                    >
                      Crop from page
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
